import { zodResolver } from '@hookform/resolvers/zod'
import { Alert, DialogContent, Stack } from '@mui/material'
import { DateTime } from 'luxon'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomDialog from '@/components/feedback/CustomDialog'
import FormDialogActions from '@/components/feedback/FormDialogActions'
import DesktopDatePickerController from '@/components/inputs/DesktopDatePickerController'
import TextFieldController from '@/components/inputs/TextFieldController'
import { TODAY } from '@/constants/dateTime'
import { useAlertContext } from '@/contexts/AlertContext'
import { logMessageInCoralogix } from '@/features/coralogix/utils/sendExceptions'
import { TIMEZONE_STOCKHOLM } from '@/features/portfolioHierarchy/endpoints/productionPlanObligationPeriods'
import { useInvalidateProductionPlanObligationPeriodQuery } from '@/features/portfolioHierarchy/hooks/useProductionPlanObligationPeriodQuery'
import { useUpdateProductionPlanObligationPeriodMutation } from '@/features/portfolioHierarchy/hooks/useUpdateProductionPlanObligationPeriodMutation'
import { getProductionPlanObligationSchema } from '@/features/portfolioHierarchy/schemas'
import type {
  EditProductionPlanObligationFormData,
  ProductionPlanObligationPeriod,
} from '@/features/portfolioHierarchy/types'
import { errorHandler } from '@/utils/errorHandler'

export type EditProductionPlanObligationPeriodDialogProps = {
  obligationPeriod: ProductionPlanObligationPeriod | null
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

function toDateTime(timestamp: string | null | undefined): DateTime | undefined {
  if (!timestamp) {
    return undefined
  }

  const dateTime = DateTime.fromISO(timestamp)
  if (!dateTime.isValid) {
    logMessageInCoralogix(`Invalid ISO timestamp: "${timestamp}" - "${dateTime.invalidReason}"`)
    return undefined
  }
  return dateTime
}

const EditProductionPlanObligationPeriodDialog = ({
  obligationPeriod,
  open,
  onClose,
  onSuccess,
}: EditProductionPlanObligationPeriodDialogProps) => {
  const { t } = useTranslation()

  const form = useForm({
    resolver: zodResolver(getProductionPlanObligationSchema(t)),
  })

  useEffect(() => {
    form.reset({
      countryCode: obligationPeriod?.countryCode ?? '',
      regulationObjectCode: obligationPeriod?.regulationObjectCode ?? '',
      updatedAt: toDateTime(obligationPeriod?.updatedAt),
      productionPlanName: obligationPeriod?.productionPlanName ?? '',
      obligationPeriod: {
        startAt: toDateTime(obligationPeriod?.obligationPeriod?.startAt),
        endAt: toDateTime(obligationPeriod?.obligationPeriod?.endAt),
      },
    })
  }, [obligationPeriod, form])

  const { updateObligationPeriod, isPending } = useUpdateProductionPlanObligationPeriodMutation()
  const { pushAlert } = useAlertContext()
  const invalidateProductionPlanObligationPeriodQuery = useInvalidateProductionPlanObligationPeriodQuery()

  if (!obligationPeriod) {
    return null
  }

  async function handleSubmit(data: EditProductionPlanObligationFormData) {
    try {
      await updateObligationPeriod(data)
      await invalidateProductionPlanObligationPeriodQuery(data.countryCode, data.regulationObjectCode)

      pushAlert({
        message: t('portfolio_hierarchy.regulation_object.production_plan_obligation.edit.update_success_message'),
        severity: 'success',
      })

      onSuccess()
    } catch (err: unknown) {
      logMessageInCoralogix('Failed to update production plan obligation period', err)
      const error = errorHandler(
        err,
        t('portfolio_hierarchy.regulation_object.production_plan_obligation.edit.update_error_message'),
      )

      pushAlert({
        message: error.message,
        severity: 'error',
      })
    }
  }

  return (
    <CustomDialog
      open={open}
      title={t('portfolio_hierarchy.regulation_object.production_plan_obligation.edit.title', {
        regulationObjectCode: obligationPeriod.regulationObjectCode,
      })}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogContent>
            <Stack sx={{ gap: 3 }}>
              <Alert severity="info">
                {t('portfolio_hierarchy.regulation_object.production_plan_obligation.edit.explanation', {
                  timezone: TIMEZONE_STOCKHOLM,
                })}
              </Alert>
              <DesktopDatePickerController
                disabled={isPending}
                label={t('portfolio_hierarchy.regulation_object.production_plan_obligation.edit.start_at_label')}
                minDate={TODAY}
                name="obligationPeriod.startAt"
                slotProps={{
                  textField: {
                    id: 'obligation-start-at',
                    required: false,
                    inputProps: {
                      'aria-label': t(
                        'portfolio_hierarchy.regulation_object.production_plan_obligation.edit.start_at_label',
                      ),
                    },
                  },
                }}
              />
              <DesktopDatePickerController
                disabled={isPending}
                label={t('portfolio_hierarchy.regulation_object.production_plan_obligation.edit.end_at_label')}
                minDate={TODAY}
                name="obligationPeriod.endAt"
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    id: 'obligation-end-at',
                    required: false,
                    inputProps: {
                      'aria-label': t(
                        'portfolio_hierarchy.regulation_object.production_plan_obligation.edit.end_at_label',
                      ),
                    },
                  },
                }}
              />
              <TextFieldController
                disabled={isPending}
                id="production-plan-name"
                label={t('portfolio_hierarchy.regulation_object.production_plan_obligation.production_plan_name')}
                name="productionPlanName"
                slotProps={{
                  htmlInput: {
                    'aria-label': t(
                      'portfolio_hierarchy.regulation_object.production_plan_obligation.production_plan_name',
                    ),
                  },
                }}
              />
            </Stack>
          </DialogContent>

          <FormDialogActions isLoading={isPending} onCancel={onClose} />
        </form>
      </FormProvider>
    </CustomDialog>
  )
}

export default EditProductionPlanObligationPeriodDialog
